/**
 * Currency mapping by country (Frontend)
 * Maps country codes to their respective currency information
 */

const currencyMapping = {
  // European Union (Euro)
  'Austria': { code: 'EUR', symbol: '€', position: 'before' },
  'Belgium': { code: 'EUR', symbol: '€', position: 'before' },
  'Cyprus': { code: 'EUR', symbol: '€', position: 'before' },
  'Estonia': { code: 'EUR', symbol: '€', position: 'before' },
  'Finland': { code: 'EUR', symbol: '€', position: 'before' },
  'France': { code: 'EUR', symbol: '€', position: 'before' },
  'Germany': { code: 'EUR', symbol: '€', position: 'before' },
  'Greece': { code: 'EUR', symbol: '€', position: 'before' },
  'Ireland': { code: 'EUR', symbol: '€', position: 'before' },
  'Italy': { code: 'EUR', symbol: '€', position: 'before' },
  'Latvia': { code: 'EUR', symbol: '€', position: 'before' },
  'Lithuania': { code: 'EUR', symbol: '€', position: 'before' },
  'Luxembourg': { code: 'EUR', symbol: '€', position: 'before' },
  'Malta': { code: 'EUR', symbol: '€', position: 'before' },
  'Netherlands': { code: 'EUR', symbol: '€', position: 'before' },
  'Portugal': { code: 'EUR', symbol: '€', position: 'before' },
  'Slovakia': { code: 'EUR', symbol: '€', position: 'before' },
  'Slovenia': { code: 'EUR', symbol: '€', position: 'before' },
  'Spain': { code: 'EUR', symbol: '€', position: 'before' },

  // UK and territories
  'UK': { code: 'GBP', symbol: '£', position: 'before' },
  'United Kingdom': { code: 'GBP', symbol: '£', position: 'before' },
  'England': { code: 'GBP', symbol: '£', position: 'before' },
  'Scotland': { code: 'GBP', symbol: '£', position: 'before' },
  'Wales': { code: 'GBP', symbol: '£', position: 'before' },
  'Northern Ireland': { code: 'GBP', symbol: '£', position: 'before' },

  // Nordic countries (non-Euro)
  'Sweden': { code: 'SEK', symbol: 'kr', position: 'after' },
  'Denmark': { code: 'DKK', symbol: 'kr', position: 'after' },
  'Norway': { code: 'NOK', symbol: 'kr', position: 'after' },
  'Iceland': { code: 'ISK', symbol: 'kr', position: 'after' },

  // Switzerland
  'Switzerland': { code: 'CHF', symbol: 'CHF', position: 'before' },

  // Americas
  'USA': { code: 'USD', symbol: '$', position: 'before' },
  'United States': { code: 'USD', symbol: '$', position: 'before' },
  'Canada': { code: 'CAD', symbol: 'CAD$', position: 'before' },
  'Mexico': { code: 'MXN', symbol: 'MXN$', position: 'before' },
  'Brazil': { code: 'BRL', symbol: 'R$', position: 'before' },
  'Argentina': { code: 'ARS', symbol: 'ARS$', position: 'before' },

  // Asia-Pacific
  'Japan': { code: 'JPY', symbol: '¥', position: 'before' },
  'China': { code: 'CNY', symbol: '¥', position: 'before' },
  'South Korea': { code: 'KRW', symbol: '₩', position: 'before' },
  'India': { code: 'INR', symbol: '₹', position: 'before' },
  'Australia': { code: 'AUD', symbol: 'A$', position: 'before' },
  'New Zealand': { code: 'NZD', symbol: 'NZ$', position: 'before' },
  'Singapore': { code: 'SGD', symbol: 'S$', position: 'before' },
  'Hong Kong': { code: 'HKD', symbol: 'HK$', position: 'before' },
  'Thailand': { code: 'THB', symbol: '฿', position: 'before' },
  'Malaysia': { code: 'MYR', symbol: 'RM', position: 'before' },
  'Indonesia': { code: 'IDR', symbol: 'Rp', position: 'before' },
  'Philippines': { code: 'PHP', symbol: '₱', position: 'before' },
  'Vietnam': { code: 'VND', symbol: '₫', position: 'after' },

  // Middle East
  'UAE': { code: 'AED', symbol: 'AED', position: 'before' },
  'Saudi Arabia': { code: 'SAR', symbol: 'SAR', position: 'before' },
  'Israel': { code: 'ILS', symbol: '₪', position: 'before' },
  'Turkey': { code: 'TRY', symbol: '₺', position: 'before' },

  // Eastern Europe (non-EU)
  'Poland': { code: 'PLN', symbol: 'zł', position: 'after' },
  'Czech Republic': { code: 'CZK', symbol: 'Kč', position: 'after' },
  'Hungary': { code: 'HUF', symbol: 'Ft', position: 'after' },
  'Romania': { code: 'RON', symbol: 'lei', position: 'after' },
  'Bulgaria': { code: 'BGN', symbol: 'лв', position: 'after' },
  'Russia': { code: 'RUB', symbol: '₽', position: 'after' },
  'Ukraine': { code: 'UAH', symbol: '₴', position: 'after' },

  // Africa
  'South Africa': { code: 'ZAR', symbol: 'R', position: 'before' },
  'Nigeria': { code: 'NGN', symbol: '₦', position: 'before' },
  'Kenya': { code: 'KES', symbol: 'KSh', position: 'before' },
  'Egypt': { code: 'EGP', symbol: 'E£', position: 'before' },
};

/**
 * Get currency info for a country
 * @param {string} country - Country name or code
 * @returns {object} Currency information with code, symbol, and position
 */
export function getCurrencyForCountry(country) {
  if (!country) {
    // Default to GBP if no country specified
    return { code: 'GBP', symbol: '£', position: 'before' };
  }

  const currency = currencyMapping[country];
  if (currency) {
    return currency;
  }

  // Default to GBP for unknown countries
  console.warn(`No currency mapping found for country: ${country}, defaulting to GBP`);
  return { code: 'GBP', symbol: '£', position: 'before' };
}

/**
 * Format an amount with the appropriate currency
 * @param {number} amount - The amount to format
 * @param {string} country - Country name or code
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, country) {
  const currency = getCurrencyForCountry(country);
  const num = parseFloat(amount || 0);

  if (currency.position === 'before') {
    return `${currency.symbol}${num.toFixed(2)}`;
  } else {
    return `${num.toFixed(2)} ${currency.symbol}`;
  }
}

const currencyUtils = {
  currencyMapping,
  getCurrencyForCountry,
  formatCurrency
};

export default currencyUtils;
